"use client";

import React from "react";
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { useCustomerPanelTranslations } from "@/lib/customer-panel-utils";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { useCustomerDashboard } from "@/hooks/useCustomerDashboard";
import StatsCard from "@/components/customer/dashboard/StatsCard";
import FavoritesSummary from "@/components/customer/dashboard/FavoritesSummary";
import RecentActivity from "@/components/customer/dashboard/RecentActivity";
import QuickActions from "@/components/customer/dashboard/QuickActions";
import UpcomingPaymentDues from "@/components/customer/dashboard/UpcomingPaymentDues";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import type { PageUrlMap } from "@/types/blog";
import type { DashboardData } from "@/services/CustomerApiService";

interface CustomerDashboardProps {
  language?: string;
  languageId?: number;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  __grapesPreview?: boolean;
}

export default function CustomerDashboard({
  language = 'en',
  languageId,
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  __grapesPreview = false,
  ...restProps
}: CustomerDashboardProps) {
  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/CustomerDashboard',
    { language, translations, pageUrls },
    isBackendRender
  );

  // Hook'ları her zaman çağır (Rules of Hooks)
  // useCustomerAuth artık backend render modunda default değerler döndürüyor
  const { customer, isAuthenticated, isLoading: authLoading } = useCustomerAuth();
  
  // Hook'ları her zaman çağır (Rules of Hooks)
  const dashboardHook = useCustomerDashboard();
  const dashboardData: DashboardData | null = isBackendRender
    ? {
        reservations_summary: {
          pending: 2,
          confirmed: 5,
          cancelled: 1,
          total: 8,
        },
        favorites_count: 3,
        recent_activity: [],
        statistics: { total_spent: 0, total_reservations: 8 },
      }
    : (dashboardHook.data as DashboardData | null);
  const isLoading = isBackendRender ? false : dashboardHook.isLoading;
  const error = isBackendRender ? null : dashboardHook.error;
  
  const data = dashboardData;

  const t = useCustomerPanelTranslations(translations);

  // Oturum kontrolü - backend render modunda değilse kontrol et
  // authLoading true ise bekle (login sonrası customer yükleniyor olabilir)
  if (!isBackendRender && !authLoading && (!isAuthenticated || !customer)) {
    // ProtectedRoute zaten yönlendirme yapacak, burada sadece null döndür
    return null;
  }
  
  // Loading state - authLoading true ise veya customer henüz yükleniyorsa veya dashboard data yükleniyorsa
  if (!isBackendRender && (authLoading || (!customer && isAuthenticated) || isLoading)) {
    return (
      <div {...backendAttributes} className="container mx-auto px-4 py-8" role="status" aria-live="polite">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" aria-hidden="true"></div>
          <span className="sr-only">{t('customer_panel.common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div {...backendAttributes} className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
          <p className="text-red-800">{t('customer_panel.common.error', 'An error occurred')}: {error}</p>
        </div>
      </div>
    );
  }

  const reservationsSummary = data?.reservations_summary || {
    pending: 0,
    confirmed: 0,
    cancelled: 0,
    total: 0,
  };

  const favoritesCount = data?.favorites_count || 0;
  const recentActivity = data?.recent_activity || [];

  const welcomeName = customer ? `${customer.first_name} ${customer.last_name}` : '';

  const upcomingDues = data?.upcoming_payment_dues ?? [];

  return (
    <div {...backendAttributes} className="space-y-6">
      {upcomingDues.length > 0 && (
        <UpcomingPaymentDues
          dues={upcomingDues}
          locale={language}
          title={t('customer_panel.dashboard.upcoming_dues', 'Upcoming payment dues')}
          emptyLabel={t('customer_panel.dashboard.no_upcoming_dues', 'No scheduled payments')}
          payLabel={t('customer_panel.invoices.pay', 'Pay')}
          dueLabel={t('customer_panel.dashboard.partial_payments_hint', 'Partial payment schedule from your reservations')}
          index={0}
        />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatsCard
          title={t('customer_panel.dashboard.pending_reservations', 'Pending Reservations')}
          value={reservationsSummary.pending}
          icon={ClockIcon}
          color="yellow"
          translations={translations}
          index={0}
        />
        <StatsCard
          title={t('customer_panel.dashboard.confirmed_reservations', 'Confirmed Reservations')}
          value={reservationsSummary.confirmed}
          icon={CheckCircleIcon}
          color="green"
          translations={translations}
          index={1}
        />
        <StatsCard
          title={t('customer_panel.dashboard.cancelled_reservations', 'Cancelled Reservations')}
          value={reservationsSummary.cancelled}
          icon={XCircleIcon}
          color="red"
          translations={translations}
          index={2}
        />
        <StatsCard
          title={t('customer_panel.dashboard.total_reservations', 'Total Reservations')}
          value={reservationsSummary.total}
          icon={ClockIcon}
          color="blue"
          translations={translations}
          index={3}
        />
      </div>

      {/* Favorites Summary and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <FavoritesSummary
          count={favoritesCount}
          language={language}
          translations={translations}
          pageUrls={pageUrls}
          defaultLanguage={defaultLanguage}
          index={0}
        />
        <QuickActions
          language={language}
          translations={translations}
          pageUrls={pageUrls}
          defaultLanguage={defaultLanguage}
          index={1}
        />
      </div>

      {/* Recent Activity */}
      <RecentActivity
        activities={recentActivity}
        translations={translations}
        index={0}
      />
    </div>
  );
}

